import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { dashboardAvanzadoAPI, formatCurrency } from '../services/api';
import { useNavigate } from 'react-router-dom';
import DashboardHorasMo from './dashboard/DashboardHorasMo';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/*
 * DashboardClaude
 * ----------------
 * Vista alternativa del dashboard ejecutivo HOMES, rediseñada con foco en:
 *  - jerarquía visual clara (hero financiero arriba, KPIs, gráficos, alertas)
 *  - gradientes, glassmorphism y micro-interacciones
 *  - una sola pantalla "scroll" en lugar de pestañas
 *  - reutilización de los datos ya cargados por Dashboard.js (sin doble fetch)
 */

const PALETTE = {
  bg: '#0F172A',
  surface: '#FFFFFF',
  primary: '#6366F1',
  primaryDark: '#4F46E5',
  violet: '#8B5CF6',
  success: '#10B981',
  successSoft: 'rgba(16, 185, 129, 0.12)',
  danger: '#EF4444',
  dangerSoft: 'rgba(239, 68, 68, 0.12)',
  warning: '#F59E0B',
  warningSoft: 'rgba(245, 158, 11, 0.12)',
  cyan: '#06B6D4',
  cyanSoft: 'rgba(6, 182, 212, 0.12)',
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate300: '#CBD5E1',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
};

const MESES_ABREV = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const MESES = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' }
];

const Icon = ({ name, size = 22, color = 'currentColor' }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };
  switch (name) {
    case 'folder':
      return (
        <svg {...common}>
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M2.5 19a6.5 6.5 0 0 1 13 0" />
          <circle cx="17" cy="9" r="2.4" />
          <path d="M15.5 19a5 5 0 0 1 6-4.8" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M4 19V5" />
          <path d="M9 19V11" />
          <path d="M14 19V8" />
          <path d="M19 19v-5" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18" />
          <circle cx="16.5" cy="14.5" r="1.2" fill={color} stroke="none" />
        </svg>
      );
    case 'bolt':
      return (
        <svg {...common}>
          <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <path d="M12 3 2 21h20z" />
          <path d="M12 10v4" />
          <circle cx="12" cy="17.5" r="0.8" fill={color} stroke="none" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="m5 12 5 5 9-11" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case 'back':
      return (
        <svg {...common}>
          <path d="M19 12H5" />
          <path d="m12 5-7 7 7 7" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg {...common}>
          <path d="M12 3v4" />
          <path d="M12 17v4" />
          <path d="M3 12h4" />
          <path d="M17 12h4" />
          <path d="m5.6 5.6 2.8 2.8" />
          <path d="m15.6 15.6 2.8 2.8" />
          <path d="m5.6 18.4 2.8-2.8" />
          <path d="m15.6 8.4 2.8-2.8" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18" />
          <path d="M8 3v4" />
          <path d="M16 3v4" />
        </svg>
      );
    case 'arrow-up-right':
      return (
        <svg {...common}>
          <path d="M7 17 17 7" />
          <path d="M8 7h9v9" />
        </svg>
      );
    default:
      return null;
  }
};

const SectionTitle = ({ children, hint }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 1.2,
      textTransform: 'uppercase',
      color: PALETTE.slate500
    }}>
      {children}
    </div>
    {hint && (
      <div style={{ fontSize: 13, color: PALETTE.slate500, marginTop: 2 }}>{hint}</div>
    )}
  </div>
);

const KpiCard = ({ icon, label, value, sub, accent, progress }) => (
  <div className="claude-card claude-kpi" style={{
    background: PALETTE.surface,
    borderRadius: 18,
    padding: 18,
    boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)',
    border: '1px solid rgba(226,232,240,0.8)',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(135deg, ${accent}10, transparent 60%)`,
      pointerEvents: 'none'
    }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{
        width: 42, height: 42, borderRadius: 12,
        background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff',
        boxShadow: `0 6px 18px ${accent}55`
      }}>
        <Icon name={icon} size={22} color="#fff" />
      </div>
      {sub && (
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: PALETTE.slate500,
          background: PALETTE.slate100,
          padding: '4px 8px',
          borderRadius: 999
        }}>
          {sub}
        </span>
      )}
    </div>
    <div style={{ marginTop: 14, position: 'relative' }}>
      <div style={{
        fontSize: 13, fontWeight: 600,
        color: PALETTE.slate500, marginBottom: 4
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 30,
        lineHeight: 1.1,
        fontWeight: 800,
        color: PALETTE.slate900,
        letterSpacing: -0.5
      }}>
        {value}
      </div>
      {typeof progress === 'number' && (
        <div style={{
          marginTop: 14,
          height: 6,
          borderRadius: 999,
          background: PALETTE.slate100,
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            width: `${Math.min(100, Math.max(0, progress))}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${accent}, ${accent}aa)`,
            borderRadius: 999,
            transition: 'width 600ms ease'
          }} />
        </div>
      )}
    </div>
  </div>
);

const DeudaRow = ({ cliente, max }) => {
  const pct = max > 0 ? (cliente.deudaTotal / max) * 100 : 0;
  const danger = cliente.deudaTotal > 0;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 110px',
      gap: 14,
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: `1px dashed ${PALETTE.slate100}`
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: PALETTE.slate900 }}>
          {cliente.nombre}
        </div>
        <div style={{ fontSize: 12, color: PALETTE.slate500, marginBottom: 6 }}>
          {cliente.expedientesCount} expediente{cliente.expedientesCount === 1 ? '' : 's'}
        </div>
        <div style={{
          height: 6,
          borderRadius: 999,
          background: PALETTE.slate100,
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.max(2, pct)}%`,
            height: '100%',
            background: danger
              ? `linear-gradient(90deg, ${PALETTE.danger}, #F87171)`
              : `linear-gradient(90deg, ${PALETTE.success}, #34D399)`
          }} />
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: danger ? PALETTE.danger : PALETTE.success
        }}>
          {formatCurrency(cliente.deudaTotal)}
        </div>
        <div style={{
          display: 'inline-block',
          marginTop: 4,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          padding: '3px 8px',
          borderRadius: 999,
          color: danger ? PALETTE.danger : PALETTE.success,
          background: danger ? PALETTE.dangerSoft : PALETTE.successSoft
        }}>
          {danger ? 'Pendiente' : 'Al día'}
        </div>
      </div>
    </div>
  );
};

const DashboardClaude = ({
  kpis,
  deudas,
  vencimientos,
  ingresosMensuales,
  resumenFinanciero: resumenFinancieroProp,
  resumenPeriodo: resumenPeriodoProp,
  loadingResumen,
  onCambiarPeriodo,
  onRefresh,
  onVolver,
}) => {
  const navigate = useNavigate();

  const hoy = new Date();
  const [periodo, setPeriodo] = useState(resumenPeriodoProp || {
    modo: 'mes',
    mes: String(hoy.getMonth() + 1),
    anio: String(hoy.getFullYear()),
  });
  const [resumen, setResumen] = useState(resumenFinancieroProp || null);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);
  const [showPeriodoModal, setShowPeriodoModal] = useState(false);
  const [draftPeriodo, setDraftPeriodo] = useState(periodo);
  const [vistaPrincipal, setVistaPrincipal] = useState('resumen');

  const buildParamsPeriodo = (p) => {
    if (!p || p.modo === 'total') return {};
    return { meses: p.mes, anio: p.anio };
  };

  // Sincroniza datos si el padre los actualiza
  useEffect(() => {
    setResumen(resumenFinancieroProp ?? null);
  }, [resumenFinancieroProp]);

  useEffect(() => {
    if (resumenPeriodoProp) setPeriodo(resumenPeriodoProp);
  }, [resumenPeriodoProp]);

  const cargarPeriodo = useCallback(async (nuevoPeriodo) => {
    setLoadingPeriodo(true);
    try {
      const res = await dashboardAvanzadoAPI.getDatos(buildParamsPeriodo(nuevoPeriodo));
      const totales = res.data?.totales || null;
      setResumen(totales);
      setPeriodo(nuevoPeriodo);
      if (onCambiarPeriodo) onCambiarPeriodo(nuevoPeriodo, totales);
    } catch (err) {
      console.error('Error cargando resumen financiero:', err);
    } finally {
      setLoadingPeriodo(false);
    }
  }, [onCambiarPeriodo]);

  const aniosDisponibles = useMemo(() => {
    const actual = new Date().getFullYear();
    const list = [];
    for (let i = actual; i >= 2020; i -= 1) list.push(String(i));
    return list;
  }, []);

  const periodoLabel = useMemo(() => {
    if (periodo.modo === 'total') return 'Total histórico';
    const mesLabel = MESES.find(m => m.value === periodo.mes)?.label || '';
    return `${mesLabel} ${periodo.anio}`;
  }, [periodo]);

  // Datos financieros consolidados
  const finanzas = useMemo(() => {
    const r = resumen || {
      montoTotal: kpis?.pagos?.montoTotalContratado || 0,
      totalAbonado: kpis?.pagos?.totalAbonado || 0,
      totalAdeudado: kpis?.pagos?.totalAdeudado || 0,
    };
    const montoTotal = Number(r.montoTotal) || 0;
    const totalAbonado = Number(r.totalAbonado) || 0;
    const totalAdeudado = Number(r.totalAdeudado) || 0;
    const pctPagado = montoTotal > 0 ? Math.round((totalAbonado / montoTotal) * 100) : 0;
    return { montoTotal, totalAbonado, totalAdeudado, pctPagado };
  }, [resumen, kpis]);

  // Cobros mensuales (line chart)
  const ingresosData = useMemo(() => {
    if (ingresosMensuales && ingresosMensuales.length > 0) {
      return {
        labels: ingresosMensuales.map(item => item.mes),
        values: ingresosMensuales.map(item => Number(item.ingresos) || 0),
      };
    }
    return { labels: MESES_ABREV, values: MESES_ABREV.map(() => 0) };
  }, [ingresosMensuales]);

  const lineChartData = useMemo(() => ({
    labels: ingresosData.labels,
    datasets: [{
      label: 'Cobros (S/)',
      data: ingresosData.values,
      fill: true,
      tension: 0.45,
      borderColor: PALETTE.primary,
      borderWidth: 3,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderColor: PALETTE.primary,
      pointBorderWidth: 2,
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const { ctx: c, chartArea } = chart;
        if (!chartArea) return 'rgba(99,102,241,0.18)';
        const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        grad.addColorStop(0, 'rgba(99,102,241,0.35)');
        grad.addColorStop(1, 'rgba(99,102,241,0.02)');
        return grad;
      },
    }],
  }), [ingresosData]);

  const lineChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: PALETTE.slate900,
        padding: 10,
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => `S/ ${Number(ctx.parsed.y).toLocaleString('es-PE')}`
        }
      }
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.18)', drawBorder: false },
        ticks: {
          color: PALETTE.slate500,
          font: { size: 11 },
          callback: (value) => 'S/ ' + Number(value).toLocaleString('es-PE'),
        }
      },
      x: {
        grid: { display: false },
        ticks: { color: PALETTE.slate500, font: { size: 11 } }
      }
    }
  }), []);

  // Donut Estados
  const estadoData = useMemo(() => {
    const e = kpis?.estado || { abiertos: 0, cerrados: 0, vencidos: 0, proximosVencer: 0 };
    return {
      labels: ['Abiertos', 'Cerrados', 'Vencidos', 'Próx. a vencer'],
      values: [e.abiertos, e.cerrados, e.vencidos, e.proximosVencer],
      colors: [PALETTE.cyan, PALETTE.success, PALETTE.danger, PALETTE.warning],
    };
  }, [kpis]);

  const donutData = useMemo(() => ({
    labels: estadoData.labels,
    datasets: [{
      data: estadoData.values,
      backgroundColor: estadoData.colors,
      borderColor: '#fff',
      borderWidth: 3,
      hoverOffset: 8,
    }]
  }), [estadoData]);

  const donutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: PALETTE.slate900,
        padding: 10,
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 8,
        displayColors: true,
      }
    }
  }), []);

  const totalExpedientes = useMemo(
    () => estadoData.values.reduce((a, b) => a + Number(b || 0), 0),
    [estadoData]
  );

  const deudasOrdenadas = useMemo(() => {
    const arr = [...(deudas || [])];
    arr.sort((a, b) => (b.deudaTotal || 0) - (a.deudaTotal || 0));
    return arr;
  }, [deudas]);
  const maxDeuda = deudasOrdenadas[0]?.deudaTotal || 0;
  const totalDeuda = useMemo(
    () => (deudas || []).reduce((acc, d) => acc + (Number(d.deudaTotal) || 0), 0),
    [deudas]
  );
  const clientesConDeuda = (deudas || []).filter(d => (d.deudaTotal || 0) > 0).length;

  const saludo = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  const fechaLarga = useMemo(() => {
    return new Date().toLocaleDateString('es-PE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      timeZone: 'America/Lima',
    });
  }, []);

  return (
    <div className="claude-dashboard" style={{
      minHeight: '100%',
      background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)',
      padding: '24px 16px 60px',
    }}>
      <style>{`
        .claude-dashboard .claude-card {
          transition: transform 250ms ease, box-shadow 250ms ease;
        }
        .claude-dashboard .claude-kpi:hover {
          transform: translateY(-3px);
          box-shadow: 0 2px 4px rgba(15,23,42,0.08), 0 16px 32px rgba(15,23,42,0.10) !important;
        }
        .claude-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 14px; border-radius: 10px;
          font-size: 13px; font-weight: 600;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 180ms ease;
        }
        .claude-btn-primary {
          background: linear-gradient(135deg, #6366F1, #8B5CF6);
          color: #fff;
          box-shadow: 0 8px 20px rgba(99,102,241,0.35);
        }
        .claude-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 26px rgba(99,102,241,0.45); }
        .claude-btn-ghost {
          background: rgba(255,255,255,0.7);
          color: #334155;
          border-color: rgba(203,213,225,0.7);
          backdrop-filter: blur(6px);
        }
        .claude-btn-ghost:hover { background: #fff; }
        .claude-btn-outline {
          background: transparent; color: #4F46E5;
          border-color: rgba(99,102,241,0.4);
        }
        .claude-btn-outline:hover { background: rgba(99,102,241,0.08); }
        .claude-grid { display: grid; gap: 16px; }
        .claude-fade-in {
          animation: claudeFadeIn 480ms ease both;
        }
        @keyframes claudeFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .claude-list-item:hover { background: rgba(99,102,241,0.05); }
        @media (max-width: 1100px) {
          .claude-cols-main { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 760px) {
          .claude-kpis-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .claude-hero-stats { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .claude-kpis-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* HEADER / HERO */}
      <div className="claude-fade-in" style={{
        position: 'relative',
        borderRadius: 24,
        padding: '26px 28px',
        background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 50%, #8B5CF6 100%)',
        color: '#fff',
        overflow: 'hidden',
        marginBottom: 22,
        boxShadow: '0 20px 50px rgba(79,70,229,0.35)'
      }}>
        <div style={{
          position: 'absolute', right: -80, top: -80,
          width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)'
        }} />
        <div style={{
          position: 'absolute', left: -60, bottom: -100,
          width: 240, height: 240, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.4), transparent 70%)'
        }} />

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
          position: 'relative', zIndex: 1
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.18)',
              padding: '6px 12px', borderRadius: 999,
              fontSize: 12, fontWeight: 600, letterSpacing: 0.5,
              marginBottom: 14,
              backdropFilter: 'blur(6px)'
            }}>
              <Icon name="sparkle" size={14} />
              Dashboard ejecutivo
            </div>
            <h1 style={{
              fontSize: 30, fontWeight: 800,
              margin: 0, letterSpacing: -0.8,
              lineHeight: 1.15
            }}>
              {saludo}, esto es lo importante hoy
            </h1>
            <p style={{
              margin: '8px 0 0', opacity: 0.9,
              fontSize: 14, textTransform: 'capitalize'
            }}>
              <Icon name="calendar" size={14} /> &nbsp;{fechaLarga}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{
              display: 'inline-flex',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 12,
              padding: 4,
              border: '1px solid rgba(255,255,255,0.25)',
            }}>
              <button
                type="button"
                onClick={() => setVistaPrincipal('resumen')}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 13,
                  background: vistaPrincipal === 'resumen' ? '#fff' : 'transparent',
                  color: vistaPrincipal === 'resumen' ? PALETTE.primaryDark : '#fff',
                }}
              >
                Resumen
              </button>
              <button
                type="button"
                onClick={() => setVistaPrincipal('mano-obra')}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 13,
                  background: vistaPrincipal === 'mano-obra' ? '#fff' : 'transparent',
                  color: vistaPrincipal === 'mano-obra' ? PALETTE.primaryDark : '#fff',
                }}
              >
                Mano de obra
              </button>
            </div>
            <button className="claude-btn claude-btn-ghost" onClick={onRefresh}>
              <Icon name="refresh" size={16} /> Actualizar
            </button>
            <button
              className="claude-btn claude-btn-ghost"
              onClick={() => navigate('/dashboard-avanzado')}
            >
              <Icon name="arrow-up-right" size={16} /> Avanzado
            </button>
            {onVolver && (
              <button
                className="claude-btn"
                onClick={onVolver}
                style={{ background: '#fff', color: PALETTE.primaryDark, fontWeight: 700 }}
              >
                <Icon name="back" size={16} /> Volver al clásico
              </button>
            )}
          </div>
        </div>

        {/* HERO FINANCIERO */}
        <div className="claude-hero-stats" style={{
          marginTop: 24,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14,
          position: 'relative', zIndex: 1
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 16,
            padding: 16,
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.85, fontWeight: 600 }}>
              CONTRATADO ({periodoLabel})
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>
              {formatCurrency(finanzas.montoTotal)}
            </div>
          </div>
          <div style={{
            background: 'rgba(16,185,129,0.18)',
            border: '1px solid rgba(16,185,129,0.4)',
            borderRadius: 16, padding: 16,
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.95, fontWeight: 600 }}>
              ABONADO
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: '#D1FAE5' }}>
              {formatCurrency(finanzas.totalAbonado)}
            </div>
          </div>
          <div style={{
            background: 'rgba(239,68,68,0.18)',
            border: '1px solid rgba(239,68,68,0.4)',
            borderRadius: 16, padding: 16,
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.95, fontWeight: 600 }}>
              ADEUDADO
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: '#FECACA' }}>
              {formatCurrency(finanzas.totalAdeudado)}
            </div>
          </div>
        </div>

        {/* Barra de % pagado */}
        <div style={{ marginTop: 18, position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 12, opacity: 0.9, marginBottom: 6, fontWeight: 600
          }}>
            <span>% pagado del contratado</span>
            <span>{finanzas.pctPagado}%</span>
          </div>
          <div style={{
            height: 10, background: 'rgba(255,255,255,0.18)',
            borderRadius: 999, overflow: 'hidden'
          }}>
            <div style={{
              width: `${finanzas.pctPagado}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #34D399, #A7F3D0)',
              borderRadius: 999,
              transition: 'width 700ms ease'
            }} />
          </div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="claude-btn claude-btn-ghost"
              style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
              onClick={() => {
                setDraftPeriodo(periodo);
                setShowPeriodoModal(true);
              }}
            >
              <Icon name="calendar" size={14} /> Cambiar periodo
            </button>
          </div>
        </div>
      </div>

      {vistaPrincipal === 'mano-obra' ? (
        <DashboardHorasMo periodo={periodo} periodoLabel={periodoLabel} />
      ) : (
        <>
      {/* KPIs */}
      <div className="claude-fade-in claude-grid claude-kpis-grid" style={{
        gridTemplateColumns: 'repeat(4, 1fr)',
        marginBottom: 22
      }}>
        <KpiCard
          icon="folder"
          accent={PALETTE.primary}
          label="Expedientes activos"
          value={kpis?.totales?.expedientes ?? 0}
          sub={`${kpis?.estado?.abiertos ?? 0} abiertos`}
        />
        <KpiCard
          icon="users"
          accent={PALETTE.cyan}
          label="Clientes registrados"
          value={kpis?.totales?.clientes ?? 0}
          sub={`${clientesConDeuda} con deuda`}
        />
        <KpiCard
          icon="chart"
          accent={PALETTE.violet}
          label="Avance de partes"
          value={`${kpis?.avance?.porcentajeGlobal ?? 0}%`}
          progress={Number(kpis?.avance?.porcentajeGlobal ?? 0)}
        />
        <KpiCard
          icon="wallet"
          accent={PALETTE.success}
          label="% pagado global"
          value={`${kpis?.pagos?.porcentajePagadoGlobal ?? 0}%`}
          progress={Number(kpis?.pagos?.porcentajePagadoGlobal ?? 0)}
        />
      </div>

      {/* COBROS + ESTADOS */}
      <div className="claude-fade-in claude-grid claude-cols-main" style={{
        gridTemplateColumns: '2fr 1fr',
        marginBottom: 22
      }}>
        {/* Cobros mensuales */}
        <div className="claude-card" style={{
          background: PALETTE.surface,
          borderRadius: 20,
          padding: 22,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8
          }}>
            <div>
              <SectionTitle>Cobros mensuales</SectionTitle>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: PALETTE.slate900 }}>
                Evolución del año en curso
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: PALETTE.slate500 }}>
                Suma de abonos registrados, mes a mes
              </p>
            </div>
            <div style={{
              padding: '6px 12px',
              background: PALETTE.slate100,
              borderRadius: 999,
              fontSize: 12, fontWeight: 700,
              color: PALETTE.slate700
            }}>
              {ingresosData.values.reduce((a, b) => a + b, 0) > 0
                ? `Total: ${formatCurrency(ingresosData.values.reduce((a, b) => a + b, 0))}`
                : 'Sin cobros aún'}
            </div>
          </div>
          <div style={{ height: 320 }}>
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>

        {/* Estado expedientes */}
        <div className="claude-card" style={{
          background: PALETTE.surface,
          borderRadius: 20,
          padding: 22,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle>Estado expedientes</SectionTitle>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: PALETTE.slate900 }}>
            Distribución actual
          </h3>
          <div style={{
            position: 'relative',
            height: 220,
            marginTop: 10
          }}>
            <Doughnut data={donutData} options={donutOptions} />
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              <div style={{ fontSize: 11, color: PALETTE.slate500, fontWeight: 600, letterSpacing: 1 }}>
                TOTAL
              </div>
              <div style={{
                fontSize: 32, fontWeight: 800,
                color: PALETTE.slate900, letterSpacing: -1
              }}>
                {totalExpedientes}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
            {estadoData.labels.map((label, i) => {
              const val = estadoData.values[i] || 0;
              const pct = totalExpedientes > 0 ? Math.round((val / totalExpedientes) * 100) : 0;
              return (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 10
                }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: estadoData.colors[i],
                    flexShrink: 0
                  }} />
                  <span style={{
                    flex: 1, fontSize: 13, fontWeight: 600,
                    color: PALETTE.slate700
                  }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: PALETTE.slate900 }}>
                    {val}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: PALETTE.slate500, minWidth: 36, textAlign: 'right'
                  }}>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ALERTAS + DEUDAS */}
      <div className="claude-fade-in claude-grid claude-cols-main" style={{
        gridTemplateColumns: '1fr 1.4fr',
      }}>
        {/* Alertas / Vencimientos */}
        <div className="claude-card" style={{
          background: PALETTE.surface,
          borderRadius: 20,
          padding: 22,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle hint="Por fecha límite de expediente y partes">
            Alertas & vencimientos
          </SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div style={{
              background: PALETTE.dangerSoft,
              border: `1px solid ${PALETTE.danger}33`,
              borderRadius: 14, padding: 14
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="alert" size={18} color={PALETTE.danger} />
                <span style={{ fontSize: 12, fontWeight: 700, color: PALETTE.danger }}>VENCIDOS</span>
              </div>
              <div style={{
                fontSize: 26, fontWeight: 800,
                color: PALETTE.danger, marginTop: 4
              }}>
                {kpis?.estado?.vencidos ?? 0}
              </div>
            </div>
            <div style={{
              background: PALETTE.warningSoft,
              border: `1px solid ${PALETTE.warning}33`,
              borderRadius: 14, padding: 14
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="bolt" size={18} color={PALETTE.warning} />
                <span style={{ fontSize: 12, fontWeight: 700, color: PALETTE.warning }}>PRÓX. A VENCER</span>
              </div>
              <div style={{
                fontSize: 26, fontWeight: 800,
                color: PALETTE.warning, marginTop: 4
              }}>
                {kpis?.estado?.proximosVencer ?? 0}
              </div>
            </div>
          </div>

          <div style={{
            fontSize: 12, fontWeight: 700,
            color: PALETTE.slate500, letterSpacing: 0.8,
            textTransform: 'uppercase', marginBottom: 6
          }}>
            Próximos 7 días
          </div>

          {vencimientos.length === 0 ? (
            <div style={{
              padding: 18, textAlign: 'center',
              background: PALETTE.slate50, borderRadius: 12,
              color: PALETTE.slate500
            }}>
              <Icon name="check" size={26} color={PALETTE.success} />
              <div style={{ marginTop: 4, fontSize: 13, fontWeight: 600 }}>
                Nada urgente en los próximos 7 días
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {vencimientos.slice(0, 5).map((v) => {
                const dias = Number(v.diasRestantes ?? 0);
                const tone = dias <= 1 ? PALETTE.danger : dias <= 3 ? PALETTE.warning : PALETTE.success;
                const toneSoft = dias <= 1 ? PALETTE.dangerSoft : dias <= 3 ? PALETTE.warningSoft : PALETTE.successSoft;
                return (
                  <div
                    key={v.id}
                    className="claude-list-item"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '6px 1fr auto',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 12,
                      background: PALETTE.slate50,
                      transition: 'background 180ms'
                    }}
                  >
                    <span style={{
                      width: 6, height: '100%',
                      borderRadius: 4, background: tone
                    }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: PALETTE.slate900,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        {v.cliente?.nombre || '—'}
                      </div>
                      <div style={{ fontSize: 12, color: PALETTE.slate500 }}>
                        Exp. {v.expediente?.numero || '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: toneSoft,
                        color: tone,
                        fontSize: 12, fontWeight: 700
                      }}>
                        {dias} día{dias === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Deudas */}
        <div className="claude-card" style={{
          background: PALETTE.surface,
          borderRadius: 20,
          padding: 22,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 8
          }}>
            <div>
              <SectionTitle>Cartera de cobranza</SectionTitle>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: PALETTE.slate900 }}>
                Deudas por cliente
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: PALETTE.slate500 }}>
                Ordenado de mayor a menor saldo pendiente
              </p>
            </div>
            <div style={{
              padding: '6px 12px',
              background: PALETTE.dangerSoft,
              border: `1px solid ${PALETTE.danger}33`,
              borderRadius: 999,
              fontSize: 12, fontWeight: 700,
              color: PALETTE.danger
            }}>
              Total: {formatCurrency(totalDeuda)}
            </div>
          </div>

          {deudasOrdenadas.length === 0 ? (
            <div style={{
              padding: 32, textAlign: 'center',
              background: PALETTE.slate50, borderRadius: 12,
              color: PALETTE.slate500, marginTop: 12
            }}>
              <Icon name="check" size={32} color={PALETTE.success} />
              <div style={{ marginTop: 6, fontWeight: 600 }}>
                No hay deudas registradas
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              {deudasOrdenadas.slice(0, 8).map(c => (
                <DeudaRow key={c.id} cliente={c} max={maxDeuda} />
              ))}
              {deudasOrdenadas.length > 8 && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button
                    className="claude-btn claude-btn-outline"
                    onClick={() => navigate('/dashboard-avanzado')}
                  >
                    Ver todos ({deudasOrdenadas.length}) <Icon name="arrow-up-right" size={14} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Loading periodo overlay sutil */}
      {(loadingPeriodo || loadingResumen) && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          background: PALETTE.slate900, color: '#fff',
          padding: '10px 16px', borderRadius: 12,
          fontSize: 13, fontWeight: 600,
          boxShadow: '0 10px 30px rgba(15,23,42,0.3)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            animation: 'claudeSpin 700ms linear infinite'
          }} />
          Actualizando resumen…
          <style>{`@keyframes claudeSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* MODAL PERIODO */}
      {showPeriodoModal && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="claude-periodo-title"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,23,42,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10050, padding: 16
          }}
          onClick={() => setShowPeriodoModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 18,
              padding: 22, width: '100%', maxWidth: 380,
              boxShadow: '0 30px 80px rgba(15,23,42,0.35)'
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 14
            }}>
              <h5 id="claude-periodo-title" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: PALETTE.slate900 }}>
                Periodo del resumen
              </h5>
              <button
                type="button"
                onClick={() => setShowPeriodoModal(false)}
                style={{
                  background: 'transparent', border: 'none',
                  cursor: 'pointer', color: PALETTE.slate500,
                  fontSize: 22, lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', borderRadius: 12,
                border: `1px solid ${draftPeriodo.modo === 'mes' ? PALETTE.primary : PALETTE.slate100}`,
                background: draftPeriodo.modo === 'mes' ? 'rgba(99,102,241,0.06)' : '#fff',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  checked={draftPeriodo.modo === 'mes'}
                  onChange={() => setDraftPeriodo(p => ({ ...p, modo: 'mes' }))}
                />
                <span style={{ fontWeight: 600, color: PALETTE.slate900 }}>Mes específico</span>
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', borderRadius: 12,
                border: `1px solid ${draftPeriodo.modo === 'total' ? PALETTE.primary : PALETTE.slate100}`,
                background: draftPeriodo.modo === 'total' ? 'rgba(99,102,241,0.06)' : '#fff',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  checked={draftPeriodo.modo === 'total'}
                  onChange={() => setDraftPeriodo(p => ({ ...p, modo: 'total' }))}
                />
                <span style={{ fontWeight: 600, color: PALETTE.slate900 }}>Total histórico</span>
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 10, marginBottom: 18 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: PALETTE.slate500 }}>Mes</label>
                <select
                  disabled={draftPeriodo.modo !== 'mes'}
                  value={draftPeriodo.mes}
                  onChange={(e) => setDraftPeriodo(p => ({ ...p, mes: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 10,
                    border: `1px solid ${PALETTE.slate100}`, marginTop: 4
                  }}
                >
                  {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: PALETTE.slate500 }}>Año</label>
                <select
                  disabled={draftPeriodo.modo !== 'mes'}
                  value={draftPeriodo.anio}
                  onChange={(e) => setDraftPeriodo(p => ({ ...p, anio: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 10,
                    border: `1px solid ${PALETTE.slate100}`, marginTop: 4
                  }}
                >
                  {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="claude-btn claude-btn-ghost"
                onClick={() => setShowPeriodoModal(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="claude-btn claude-btn-primary"
                disabled={loadingPeriodo}
                onClick={() => {
                  setShowPeriodoModal(false);
                  cargarPeriodo(draftPeriodo);
                }}
              >
                {loadingPeriodo ? 'Aplicando…' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DashboardClaude;
